import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { database } from '../firebaseConfig';
import { ref, onValue } from 'firebase/database';
import { CLOUD_FUNCTION_URLS, authenticatedFetch } from '../utils/cloudFunctions';
import './CrmIntegrationsSettings.css';

const PROVIDERS = [
  {
    id: 'close',
    name: 'Close CRM',
    tagline: 'Domain-based duplicate guard + auto-create leads on send',
    icon: 'fa-bullseye',
    color: '#0c8b68',
    available: true,
  },
  {
    id: 'hubspot',
    name: 'HubSpot',
    tagline: 'Coming soon',
    icon: 'fa-h-square',
    color: '#ff7a59',
    available: false,
  },
  {
    id: 'pipedrive',
    name: 'Pipedrive',
    tagline: 'Coming soon',
    icon: 'fa-chart-column',
    color: '#1a1a1a',
    available: false,
  },
];

const DEFAULT_WHITELIST = 'SalesGod';

const autoDetectLeadSource = (fields) => {
  if (!Array.isArray(fields)) return null;
  const match = fields.find((f) => /^lead[\s_-]?source$/i.test(f.name || ''));
  return match ? match.id : null;
};

const CrmIntegrationsSettings = ({ userId }) => {
  const [loading, setLoading] = useState(true);
  const [closeConfig, setCloseConfig] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState(1);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [modalBusy, setModalBusy] = useState(false);
  const [modalError, setModalError] = useState('');
  const [availableFields, setAvailableFields] = useState([]);
  const [selectedFieldId, setSelectedFieldId] = useState('');
  const [whitelistValue, setWhitelistValue] = useState(DEFAULT_WHITELIST);
  const [saving, setSaving] = useState(false);
  const [saveFlash, setSaveFlash] = useState('');
  const flashTimerRef = useRef(null);

  const [testInput, setTestInput] = useState('');
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState('');

  useEffect(() => {
    if (!userId) return;
    const closeRef = ref(database, `/World/${userId}/integrations/crm/close`);
    const unsub = onValue(closeRef, (snap) => {
      const val = snap.val();
      setCloseConfig(val || null);
      setLoading(false);
    });
    return () => unsub();
  }, [userId]);

  const flashSaved = (message = 'Saved') => {
    setSaveFlash(message);
    clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setSaveFlash(''), 2200);
  };

  useEffect(() => () => clearTimeout(flashTimerRef.current), []);

  const openConnectModal = () => {
    setModalOpen(true);
    setModalStep(1);
    setApiKeyInput('');
    setModalError('');
    setAvailableFields([]);
    setSelectedFieldId('');
    setWhitelistValue(closeConfig?.whitelistValue || DEFAULT_WHITELIST);
  };

  const closeModal = () => {
    if (modalBusy) return;
    setModalOpen(false);
  };

  const handleTestAndLoadFields = async () => {
    const trimmed = apiKeyInput.trim();
    if (!trimmed) {
      setModalError('Please paste your Close API key first.');
      return;
    }
    setModalBusy(true);
    setModalError('');
    try {
      const res = await authenticatedFetch(CLOUD_FUNCTION_URLS.crmConnectClose(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apiKey: trimmed, userId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setModalError(data.error || 'Could not connect to Close. Check your API key.');
        setModalBusy(false);
        return;
      }
      const fields = Array.isArray(data.fields) ? data.fields : [];
      setAvailableFields(fields);
      const suggestion = (data.suggestedField && data.suggestedField.id)
        || (data.existingConfig && data.existingConfig.leadSourceFieldId)
        || autoDetectLeadSource(fields)
        || '';
      setSelectedFieldId(suggestion);
      if (data.existingConfig && data.existingConfig.whitelistValue) {
        setWhitelistValue(data.existingConfig.whitelistValue);
      }
      setModalStep(2);
    } catch (err) {
      console.error('[CRM-Settings] connect failed:', err);
      setModalError('Connection failed. Please try again.');
    } finally {
      setModalBusy(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!selectedFieldId) {
      setModalError('Please choose the Lead Source custom field.');
      return;
    }
    const whitelistTrim = (whitelistValue || '').trim();
    if (!whitelistTrim) {
      setModalError('Whitelist value cannot be empty.');
      return;
    }
    setModalBusy(true);
    setModalError('');
    try {
      const field = availableFields.find((f) => f.id === selectedFieldId);
      const res = await authenticatedFetch(CLOUD_FUNCTION_URLS.crmSaveCloseConfig(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          leadSourceFieldId: selectedFieldId,
          leadSourceFieldName: field ? field.name : '',
          whitelistValue: whitelistTrim,
          enabled: closeConfig?.enabled !== false,
          autoCreateOnSend: closeConfig?.autoCreateOnSend !== false,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setModalError(data.error || 'Failed to save configuration.');
        setModalBusy(false);
        return;
      }
      setModalOpen(false);
      flashSaved('Connected & saved');
    } catch (err) {
      console.error('[CRM-Settings] save failed:', err);
      setModalError('Save failed. Please try again.');
    } finally {
      setModalBusy(false);
    }
  };

  const patchConfig = useCallback(async (patch) => {
    setSaving(true);
    try {
      const res = await authenticatedFetch(CLOUD_FUNCTION_URLS.crmSaveCloseConfig(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          leadSourceFieldId: closeConfig?.leadSourceFieldId,
          leadSourceFieldName: closeConfig?.leadSourceFieldName || '',
          whitelistValue: closeConfig?.whitelistValue || DEFAULT_WHITELIST,
          enabled: closeConfig?.enabled !== false,
          autoCreateOnSend: closeConfig?.autoCreateOnSend !== false,
          ...patch,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        console.warn('[CRM-Settings] patch failed:', data.error);
        return;
      }
      flashSaved();
    } finally {
      setSaving(false);
    }
  }, [closeConfig]);

  const handleTestDomain = async () => {
    const trimmed = testInput.trim();
    if (!trimmed) {
      setTestError('Enter an email address or domain to test.');
      return;
    }
    setTestBusy(true);
    setTestError('');
    setTestResult(null);
    try {
      const res = await authenticatedFetch(CLOUD_FUNCTION_URLS.crmTestDomain(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, email: trimmed }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setTestError(data.error || 'Test failed.');
        setTestBusy(false);
        return;
      }
      setTestResult(data);
    } catch (err) {
      console.error('[CRM-Settings] test failed:', err);
      setTestError('Network error — try again.');
    } finally {
      setTestBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Close CRM? API key and domain cache will be removed.')) return;
    setSaving(true);
    try {
      const res = await authenticatedFetch(CLOUD_FUNCTION_URLS.crmDisconnect(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, provider: 'close' }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) flashSaved('Disconnected');
    } finally {
      setSaving(false);
    }
  };

  // DSGVO-Architektur: der echte apiKey liegt im server-only Pfad /SecretIntegrations/...
  // und wird per RTDB-Rules niemals zum Client ausgeliefert. Für die UI-Detection
  // nutzen wir stattdessen `connected` + `apiKeyPreview` (maskiert) als Proxy.
  const isConnected = !!(closeConfig && closeConfig.connected && closeConfig.apiKeyPreview && closeConfig.leadSourceFieldId);
  const isKeySaved = !!(closeConfig && closeConfig.connected && closeConfig.apiKeyPreview);

  if (loading) {
    return (
      <div className="crm">
        <div className="crm-loading">
          <span className="crm-spinner" />
          <p>Loading CRM…</p>
        </div>
      </div>
    );
  }

  const comingSoon = PROVIDERS.filter((p) => !p.available);
  const lead = !isKeySaved
    ? 'Close is not connected. Link it once so the cleaner can skip people who are already in your CRM.'
    : !isConnected
    ? 'Close has a key, but the Lead Source field is not mapped yet. Finish setup so the guard can run.'
    : closeConfig.enabled === false
    ? 'Close is connected, but the domain check is off. Contacts will not be compared against Close.'
    : 'Close is connected. Contacts whose domain is already in Close — and not marked as yours — are skipped.';

  return (
    <div className="crm">
      <header className="crm-page-head">
        <div>
          <h3 className="crm-page-title">CRM</h3>
          <p className="crm-page-sub">
            Check new contacts against Close so you don’t mail people who are already there. Leads you created stay.
          </p>
        </div>
        {saveFlash ? (
          <span className="crm-badge is-saved">{saveFlash}</span>
        ) : (
          <span className="crm-badge">{isConnected ? 'Close connected' : isKeySaved ? 'Setup incomplete' : 'Not connected'}</span>
        )}
      </header>

      <section className="crm-card">
        <div className={`crm-status ${!isConnected ? 'is-off' : ''}`}>
          <div className="crm-status-lead">
            <span className={`crm-status-dot ${isConnected && closeConfig.enabled !== false ? 'on' : 'off'}`} aria-hidden />
            <span>{lead}</span>
          </div>
          {isKeySaved && (
            <div className="crm-status-grid">
              <div className="crm-status-cell">
                <span className="crm-status-cell-label">Account</span>
                <span className="crm-status-cell-value">{closeConfig.connectedEmail || 'Close'}</span>
                <span className="crm-status-cell-desc">{closeConfig.apiKeyPreview || 'Key saved'}</span>
              </div>
              <div className="crm-status-cell">
                <span className="crm-status-cell-label">Lead source field</span>
                <span className="crm-status-cell-value">
                  {closeConfig.leadSourceFieldName || closeConfig.leadSourceFieldId || 'Not mapped'}
                </span>
                <span className="crm-status-cell-desc">
                  {isConnected ? 'Used to tell your leads from theirs.' : 'Finish setup to map this field.'}
                </span>
              </div>
              <div className="crm-status-cell">
                <span className="crm-status-cell-label">Your tag</span>
                <span className="crm-status-cell-value">{closeConfig.whitelistValue || DEFAULT_WHITELIST}</span>
                <span className="crm-status-cell-desc">Contacts with this tag are always kept.</span>
              </div>
              <div className="crm-status-cell">
                <span className="crm-status-cell-label">Guard</span>
                <span className="crm-status-cell-value">{closeConfig.enabled !== false ? 'On' : 'Off'}</span>
                <span className="crm-status-cell-desc">
                  {closeConfig.enabled !== false ? 'Cleaner skips foreign Close domains.' : 'Turn on below to start checking.'}
                </span>
              </div>
            </div>
          )}
        </div>

        {!isKeySaved && (
          <div className="crm-empty">
            <p>
              Connect Close once. After that, every cleaner run skips domains that already exist in Close — unless they
              are tagged as yours.
            </p>
            <button type="button" className="altBtn altBtn--save" onClick={openConnectModal}>
              Connect Close
            </button>
          </div>
        )}

        {isKeySaved && (
          <>
            {!isConnected && (
              <div className="crm-warn">
                Setup is incomplete. Open setup and map the Lead Source field so the guard can run.
              </div>
            )}

            <div className="crm-row">
              <div className="crm-row-copy">
                <strong>Skip people already in Close</strong>
                <span>The cleaner blocks contacts whose domain exists in Close with a different Lead Source.</span>
              </div>
              <button
                type="button"
                className={`crm-toggle ${closeConfig.enabled !== false ? 'on' : ''}`}
                onClick={() => patchConfig({ enabled: closeConfig.enabled === false })}
                disabled={saving || !isConnected}
                aria-pressed={closeConfig.enabled !== false}
              >
                <span className="crm-toggle-knob" />
              </button>
            </div>

            <div className="crm-row">
              <div className="crm-row-copy">
                <strong>Create a Close lead on first send</strong>
                <span>
                  When you send the first mail, we create a Close lead tagged{' '}
                  <code>{closeConfig.whitelistValue || DEFAULT_WHITELIST}</code>
                  {' '}so your own contacts are never blocked later.
                </span>
              </div>
              <button
                type="button"
                className={`crm-toggle ${closeConfig.autoCreateOnSend !== false ? 'on' : ''}`}
                onClick={() => patchConfig({ autoCreateOnSend: closeConfig.autoCreateOnSend === false })}
                disabled={saving || !isConnected}
                aria-pressed={closeConfig.autoCreateOnSend !== false}
              >
                <span className="crm-toggle-knob" />
              </button>
            </div>

            {isConnected && (
              <div className="crm-tester">
                <div className="crm-tester-head">
                  <strong>Try a domain</strong>
                  <span>Enter an email or domain. We ask Close once and show what would happen on send.</span>
                </div>
                <div className="crm-tester-row">
                  <input
                    type="text"
                    className="crm-input"
                    placeholder="max@mustermann.com or mustermann.com"
                    value={testInput}
                    onChange={(e) => {
                      setTestInput(e.target.value);
                      if (testError) setTestError('');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !testBusy) handleTestDomain();
                    }}
                    disabled={testBusy}
                  />
                  <button
                    type="button"
                    className="altBtn altBtn--save"
                    onClick={handleTestDomain}
                    disabled={testBusy || !testInput.trim()}
                  >
                    {testBusy ? 'Testing…' : 'Test'}
                  </button>
                </div>

                {testError && <div className="crm-error">{testError}</div>}

                {testResult && (
                  <div className={`crm-test-result ${testResult.allowed ? 'is-ok' : 'is-block'}`}>
                    <div className="crm-test-result-head">
                      <span>{testResult.allowed ? 'Would send' : 'Would skip'}</span>
                      <span className="crm-chip">{testResult.status}</span>
                      <span className="crm-test-ms">{testResult.latencyMs} ms</span>
                    </div>
                    <div className="crm-status-grid crm-status-grid--inset">
                      <div className="crm-status-cell">
                        <span className="crm-status-cell-label">Domain</span>
                        <span className="crm-status-cell-value">{testResult.domain}</span>
                      </div>
                      {testResult.leadId && (
                        <div className="crm-status-cell">
                          <span className="crm-status-cell-label">Close lead</span>
                          <span className="crm-status-cell-value">{testResult.leadId}</span>
                        </div>
                      )}
                      <div className="crm-status-cell">
                        <span className="crm-status-cell-label">Lead source</span>
                        <span className="crm-status-cell-value">
                          {testResult.leadSourceValue || (testResult.status === 'crm_not_found' ? 'No lead' : 'Empty')}
                        </span>
                      </div>
                      <div className="crm-status-cell">
                        <span className="crm-status-cell-label">Your tag</span>
                        <span className="crm-status-cell-value">{testResult.whitelistValue}</span>
                      </div>
                    </div>
                    <p className="crm-test-summary">{testResult.summary}</p>
                  </div>
                )}
              </div>
            )}

            <div className="crm-actions">
              <button type="button" className="altBtn altBtn--petrol" onClick={openConnectModal}>
                Edit setup
              </button>
              <button type="button" className="altBtn altBtn--petrol" onClick={handleDisconnect} disabled={saving}>
                Disconnect
              </button>
            </div>
          </>
        )}
      </section>

      {comingSoon.length > 0 && (
        <section className="crm-soon">
          {comingSoon.map((p) => (
            <div key={p.id} className="crm-soon-item">
              <strong>{p.name}</strong>
              <span>Coming soon</span>
            </div>
          ))}
        </section>
      )}

      {modalOpen && createPortal(
        <div className="crm-modal-overlay" onClick={closeModal}>
          <div className="crm-modal" onClick={(e) => e.stopPropagation()}>
            <header className="crm-modal-head">
              <div>
                <span className="crm-modal-eyebrow">Close</span>
                <h3>Connect Close</h3>
                <p>Paste the API key, then tell us which field marks a lead as yours.</p>
              </div>
              <button type="button" className="crm-modal-close" onClick={closeModal} disabled={modalBusy} aria-label="Close">
                <i className="fa fa-xmark" />
              </button>
            </header>

            <div className="crm-modal-steps">
              <span className={modalStep >= 1 ? 'is-on' : ''}>1 · API key</span>
              <span className={modalStep >= 2 ? 'is-on' : ''}>2 · Lead source</span>
            </div>

            <div className="crm-modal-body">
              {modalStep === 1 && (
                <div className="crm-field">
                  <label className="crm-field-label">Close API key</label>
                  <input
                    type="password"
                    className="crm-input"
                    placeholder="api_…"
                    value={apiKeyInput}
                    onChange={(e) => setApiKeyInput(e.target.value)}
                    autoFocus
                  />
                  <p className="crm-field-hint">
                    Create one under Close → Settings → API Keys.{' '}
                    <a href="https://help.close.com/docs/api-keys" target="_blank" rel="noreferrer">Documentation</a>
                    . The key stays on the server and is never shown again.
                  </p>
                </div>
              )}

              {modalStep === 2 && (
                <>
                  <div className="crm-field">
                    <label className="crm-field-label">Lead source field</label>
                    <select
                      className="crm-input"
                      value={selectedFieldId}
                      onChange={(e) => setSelectedFieldId(e.target.value)}
                    >
                      <option value="">Choose the field that tags the lead’s origin</option>
                      {availableFields.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.name} {f.type ? `(${f.type})` : ''}
                        </option>
                      ))}
                    </select>
                    <p className="crm-field-hint">
                      We read this field for every domain and compare it to your tag. Fields named “Lead Source” are picked automatically.
                    </p>
                  </div>
                  <div className="crm-field">
                    <label className="crm-field-label">Your tag</label>
                    <input
                      type="text"
                      className="crm-input"
                      value={whitelistValue}
                      onChange={(e) => setWhitelistValue(e.target.value)}
                    />
                    <p className="crm-field-hint">
                      Exact match, case-insensitive. If this field equals <code>{whitelistValue || DEFAULT_WHITELIST}</code>, the contact is treated as yours and kept.
                    </p>
                  </div>
                </>
              )}

              {modalError && <div className="crm-error">{modalError}</div>}
            </div>

            <footer className="crm-modal-foot">
              {modalStep === 1 ? (
                <>
                  <button type="button" className="altBtn altBtn--petrol" onClick={closeModal} disabled={modalBusy}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="altBtn altBtn--save"
                    onClick={handleTestAndLoadFields}
                    disabled={modalBusy || !apiKeyInput.trim()}
                  >
                    {modalBusy ? 'Testing…' : 'Continue'}
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className="altBtn altBtn--petrol" onClick={() => setModalStep(1)} disabled={modalBusy}>
                    Back
                  </button>
                  <button
                    type="button"
                    className="altBtn altBtn--save"
                    onClick={handleSaveConfig}
                    disabled={modalBusy || !selectedFieldId}
                  >
                    {modalBusy ? 'Saving…' : 'Save'}
                  </button>
                </>
              )}
            </footer>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CrmIntegrationsSettings;
